import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { validate } from 'class-validator';
import pino from 'pino';

import { Environment, GetEndpoint } from './endpoint';
import { ExistingQuestionnaire } from './existing-questionnaire';
import { NewQuestionnaire } from './new-questionnaire';
import { QuestionnaireAndAnswers } from './questionnaire-and-answers';

export type AuthProviderDescriptor = () => string;
export type AuthTokenProvider = (forceRefreshToken: boolean) => Promise<string>;

const HEADERS = {
    AUTH_PROVIDER: 'x-auth-provider'
};

export class Client {
    static readonly questionnairePath = 'questionnaires';
    private service: AxiosInstance;
    private readonly serviceUrl: string;
    private readonly siteUrl: string;

    constructor(
        private readonly region: string,
        private readonly environment: Environment,
        private readonly getToken: AuthTokenProvider,
        private readonly getAuthProvider: AuthProviderDescriptor,
        private readonly logger: pino.Logger | undefined
    ) {
        this.serviceUrl = GetEndpoint(environment).serviceUrl;
        this.siteUrl = GetEndpoint(environment).siteUrl;
        this.service = axios.create({
            baseURL: this.serviceUrl,
            headers: {
                'X-Requested-With': 'XMLHttpRequest'
            },
        });

        this.service.interceptors.request.use(async (config: AxiosRequestConfig) => {
            const authToken = await getToken(false);
            if (config && config.headers) {
                config.headers.Authorization = `Bearer ${authToken}`;
                config.headers[HEADERS.AUTH_PROVIDER] = getAuthProvider();
            } else {
                logger?.error('Axios config not available to set auth header');
            }
            return config;
        }, undefined);

        this.service.interceptors.response.use(undefined, async (err: any) => {
            logger?.error('MDCService response error intercepted.');
            if (err && err.config && err.response && err.response.status && err.response.status === 401) {
                const authToken = await getToken(true);
                err.config.headers.Authorization = `Bearer ${authToken}`;
                err.config.headers[HEADERS.AUTH_PROVIDER] = getAuthProvider();

                return axios.request(err.config);
            } else {
                throw err; // throwing will return a rejected promise since this is an async function
            }
        });
    }

    public getQuestionnaires = async (sourceResourceId?: string): Promise<ExistingQuestionnaire[]> => {
        const params: {sourceResourceId?: string} = {};
        if (sourceResourceId) {
            params.sourceResourceId = sourceResourceId;
        }
        const queryParams: string = new URLSearchParams(params).toString();
        const result = await this.service.get(`/${Client.questionnairePath}?${queryParams}`);
        return result.data as ExistingQuestionnaire[];
    };

    public createQuestionnaire = async (questionnaire: Partial<NewQuestionnaire>): Promise<ExistingQuestionnaire> => {
        await validate(questionnaire);
        const result = await this.service.post(`/${Client.questionnairePath}`, questionnaire);
        return result.data as ExistingQuestionnaire;
    };

    public createAssignments = async (questionnaireId: string, userEmails: string[]) => {
        const result = await this.service.post(`/${Client.questionnairePath}/${questionnaireId}/assignments/batchCreate`, userEmails);
        return result;
    };

    public getAnswers = async (questionnaireId: string, distributionAnalysis?: boolean): Promise<QuestionnaireAndAnswers> => {
        const params: {analysis?: string} = {};
        if (distributionAnalysis) {
            params.analysis = 'distribution';
        }
        const queryParams: string = new URLSearchParams(params).toString();
        const result = await this.service.get(`/${Client.questionnairePath}/${questionnaireId}/answers?${queryParams}`);
        return result.data as QuestionnaireAndAnswers;
    };

    public patchQuestionnaire = async (questionnaireId: string, updatedQuestionnaire: Partial<ExistingQuestionnaire>) => {
        const result = await this.service.patch(`/${Client.questionnairePath}/${questionnaireId}`, updatedQuestionnaire);
        return result.data as ExistingQuestionnaire;
    };

    public getAssignments = async (questionnaireId: string) => {
        const result = await this.service.get(`/${Client.questionnairePath}/${questionnaireId}/assignments`);
        return result.data;
    };

    public createAssignment = async (questionnaireId: string, userEmail: string) => {
        const result = await this.service.post(`/${Client.questionnairePath}/${questionnaireId}/assignment/${userEmail}`);
        return result.data;
    };

    public deleteAssignment = async (questionnaireId: string, userEmail: string) => {
        const result = await this.service.delete(`/${Client.questionnairePath}/${questionnaireId}/assignment/${userEmail}`);
        return result;
    };

    public deleteQuestionnaires = async (questionnaireIds: string[]) => {
        const result = await this.service.patch(`/${Client.questionnairePath}/delete`, questionnaireIds);
        return result;
    };

    public getParticipantsUrl = (questionnaireId: string) : string => {
        return `${this.siteUrl}/${Client.questionnairePath}/${questionnaireId}`;
    };

    public getSponsorUrl = (questionnaireId: string) : string => {
        return `${this.siteUrl}/${Client.questionnairePath}/${questionnaireId}/participants`;
    };
}
